import type { ReactNode, KeyboardEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { useAuth } from '@/features/auth/authContext';
import { DensePagePanel, DensePageShell } from '@/components/layout/DensePageShell';
import {
  FINANCE_AREAS,
  FINANCE_LINKS,
  canOpenFinanceLink,
  financeAreaForLocation,
} from './financeWorkspace.navigation';
import './finance-workspace.css';

export function FinanceWorkspaceShell({ children }: { children: ReactNode }) {
  const { permissions } = useAuth();
  const location = useLocation();
  const area = financeAreaForLocation(location.pathname, location.search);
  const areas = FINANCE_AREAS.filter((item) =>
    FINANCE_LINKS[item.id].some((link) => canOpenFinanceLink(link, permissions)),
  );
  const links = FINANCE_LINKS[area].filter((link) => canOpenFinanceLink(link, permissions));
  const preview = import.meta.env.DEV && import.meta.env.VITE_READ_ONLY_PREVIEW === 'true';
  function arrowNavigation(event: KeyboardEvent<HTMLElement>) {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    const anchors = Array.from(event.currentTarget.querySelectorAll<HTMLAnchorElement>('a'));
    const index = anchors.indexOf(event.target as HTMLAnchorElement);
    if (index < 0 || !anchors.length) return;
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? anchors.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + anchors.length) % anchors.length;
    event.preventDefault();
    anchors[next]?.focus();
    anchors[next]?.click();
  }
  return (
    <DensePageShell className="finance-workspace">
      {preview ? (
        <div className="finance-workspace__preview" role="status">
          Live development preview · real API reads · business changes disabled
        </div>
      ) : null}
      <nav
        className="finance-workspace__nav"
        aria-label="Finance areas"
        onKeyDown={arrowNavigation}
      >
        {areas.map((item) => (
          <Link
            key={item.id}
            to={item.to}
            aria-current={area === item.id ? 'page' : undefined}
            className={area === item.id ? 'is-active' : ''}
          >
            {item.label}
          </Link>
        ))}
      </nav>
      {area !== 'overview' && links.length > 1 ? (
        <nav
          className="finance-workspace__local"
          aria-label={`${FINANCE_AREAS.find((item) => item.id === area)?.label} sections`}
        >
          {links.map((link) => {
            const target = new URL(link.to, 'https://local.invalid');
            const selected =
              target.pathname === location.pathname &&
              (!target.search || target.search === location.search);
            return (
              <Link key={link.to} to={link.to} aria-current={selected ? 'page' : undefined}>
                {link.label}
              </Link>
            );
          })}
        </nav>
      ) : null}
      <DensePagePanel className="finance-workspace__content">{children}</DensePagePanel>
    </DensePageShell>
  );
}
